/**
 * Shared KSS series display helpers (CMS + Tracker).
 */

function inferRecurrenceType(task) {
  if (task?.recurrenceType) {
    return task.recurrenceType;
  }
  const rule = task?.repeatRules?.[0];
  if (!rule || rule.frequency === 'MINUTELY') {
    return null;
  }
  if (rule.frequency === 'MONTHLY') return 'monthly';
  if (rule.frequency === 'WEEKLY') return 'weekly';
  if (rule.interval) return 'custom';
  return 'daily';
}

function formatDayCodes(codes, t) {
  if (!codes?.length) return '';
  if (!t) return codes.join(', ');
  return codes
    .map((code) => t(`KssRecurringTaskComponent.daysShort.${String(code).toUpperCase()}`) || code)
    .join(', ');
}

export function formatKssSeriesEnd(task, t) {
  if (!task?.isRecurring) {
    return '—';
  }
  if (task.seriesEnded || task.status === 'ended') {
    return t ? t('kssSeriesDisplay.seriesEnded') : 'Series ended';
  }
  if (task.isEndless) {
    return t ? t('kssSeriesDisplay.endless') : 'Endless';
  }
  const raw = task.seriesEndDate || task.repeatRules?.[0]?.end;
  if (!raw) {
    return t ? t('kssSeriesDisplay.endless') : 'Endless';
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleDateString();
}

/** Localized rhythm label — ignores English `recurrenceLabel` from the API. */
export function formatKssRecurrenceLabel(task, t) {
  if (!task?.isRecurring) {
    return t ? t('kssSeriesDisplay.oneTime') : 'One-time';
  }

  const rule = task.repeatRules?.[0];
  const type = inferRecurrenceType(task);
  const workingDaysOnly = type === 'daily' && task.includeWeekends === false;
  const days = rule?.byDayOfWeek || task.recurrenceByDayOfWeek || [];
  const dayLabels = formatDayCodes(days, t);

  if (!t) {
    if (type === 'monthly') return 'Monthly';
    if (type === 'weekly') return dayLabels ? `Weekly (${dayLabels})` : 'Weekly';
    if (type === 'custom') return 'Custom';
    return workingDaysOnly ? 'Daily (Mon–Fri)' : 'Daily (all days)';
  }

  switch (type) {
    case 'daily':
      if (workingDaysOnly) {
        return t('KssRecurringTaskComponent.daily');
      }
      return t('kssSeriesDisplay.dailyAllDays');
    case 'weekly':
      if (dayLabels) {
        return t('kssSeriesDisplay.weeklyWithDays', { days: dayLabels });
      }
      return t('KssRecurringTaskComponent.weekly');
    case 'monthly': {
      const week = rule?.byWeekOfMonth ?? task.recurrenceByWeekOfMonth;
      if (week != null && dayLabels) {
        return t('kssSeriesDisplay.monthlyWithWeekDay', { week, days: dayLabels });
      }
      if (dayLabels) {
        return t('kssSeriesDisplay.monthlyWithDays', { days: dayLabels });
      }
      return t('KssRecurringTaskComponent.monthly');
    }
    case 'custom': {
      const interval = rule?.interval ?? task.recurrenceInterval ?? 1;
      if (days.length) {
        return t('kssSeriesDisplay.customEveryWeeks', { n: interval, days: dayLabels });
      }
      if (workingDaysOnly) {
        return t('kssSeriesDisplay.customEveryDaysWorking', { n: interval });
      }
      return t('kssSeriesDisplay.customEveryDays', { n: interval });
    }
    default:
      return t('KssRecurringTaskComponent.daily');
  }
}

export function formatKssEventDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export function formatKssUpcomingPreview(task, max = 6) {
  const preview = task?.upcomingPreview;
  if (Array.isArray(preview) && preview.length > 0) {
    return preview.slice(0, max).map((e) => formatKssEventDate(e.date_eventBegin));
  }
  const events = (task?.taskEvents || [])
    .filter((e) => e.status === 'scheduled')
    .sort(
      (a, b) => new Date(a.date_eventBegin).getTime() - new Date(b.date_eventBegin).getTime(),
    )
    .slice(0, max);
  return events.map((e) => formatKssEventDate(e.date_eventBegin));
}

export function getKssPlannedScheduledCount(task) {
  if (typeof task?.plannedScheduledCount === 'number') {
    return task.plannedScheduledCount;
  }
  return (task?.taskEvents || []).filter((e) => e.status === 'scheduled').length;
}
